import { memo, useCallback, useLayoutEffect, useMemo, useState, forwardRef, type ReactNode } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import reject from 'lodash/reject';
import transform from 'lodash/transform';
import toSafeInteger from 'lodash/toSafeInteger';
import toString from 'lodash/toString';
import { useIntl } from 'react-intl';
// Material UI imports
import Typography from '@mui/material/Typography';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select, { type SelectProps, type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// local imports
import { LookupItem } from '../models/lookupItem';
import { DEFAULT_MENU_PROPS } from '../helpers/menus';
// SCSS imports
import { root, shortRoot, placeholder } from '../styles/modules/Lookup.module.scss';

type FilterSelectorProps = {
  readonly type: string;
  readonly ariaLabel?: string;
  readonly choices?: LookupItem[] | null;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly disabled?: boolean | null;
  readonly required?: boolean | null;
  readonly short?: boolean | null;
  readonly myId?: number;
  readonly controlSx?: FormControlProps['sx'];
  readonly selectSx?: SelectProps['sx'];
  readonly multiple?: false; // single-select (default)
  readonly value: number;
  readonly onChange: (id: number) => void;
} | {
  readonly type: string;
  readonly ariaLabel?: string;
  readonly choices?: LookupItem[] | null;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly disabled?: boolean | null;
  readonly required?: boolean | null;
  readonly short?: boolean | null;
  readonly myId?: number;
  readonly controlSx?: FormControlProps['sx'];
  readonly selectSx?: SelectProps['sx'];
  // multi-select
  readonly multiple: true;
  readonly values: number[];
  readonly onChangeMultiple: (ids: number[]) => void;
};

const FilterSelector = forwardRef<HTMLDivElement, FilterSelectorProps>(({
  type,
  ariaLabel,
  choices,
  error,
  helperText,
  disabled = false,
  required = false,
  short = false,
  myId,
  controlSx,
  selectSx,
  ...rest
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const isMultiple = rest.multiple === true;
  const value = isMultiple ? 0 : (rest as { value: number }).value;
  const onChange = isMultiple ? undefined : (rest as { onChange: (id: number) => void }).onChange;
  const values = isMultiple ? (rest as { values: number[] }).values : undefined;
  const onChangeMultiple = isMultiple ? (rest as { onChangeMultiple: (ids: number[]) => void }).onChangeMultiple : undefined;

  const [selected, setSelected] = useState<number[]>(values || []);

  useLayoutEffect(() => {
    if (isMultiple && values) setSelected(values);
  }, [isMultiple, values]);

  const handleChange = useCallback((event: SelectChangeEvent<number | number[]>) => {
    if (isMultiple) {
      const val = event.target.value as number[];
      setSelected(val.map(toSafeInteger).filter(Boolean));
    } else {
      onChange?.(toSafeInteger(event.target.value));
    }
  }, [isMultiple, onChange]);

  const handleClose = useCallback(() => {
    if (onChangeMultiple) onChangeMultiple(selected);
  }, [onChangeMultiple, selected]);

  const displayChoices = useMemo(() => myId ? reject(choices, ['id', myId]) : choices, [choices, myId]);

  const names: Record<string, string> = useMemo(
    () => transform(choices || [], (result, { id, name, title }) => {
      result[toString(id)] = myId
        ? formatMessage({ id: `filter.${type}.name` }, { name: name || title || '', is_my: myId === id })
        : name || title || '';
    }, {} as Record<string, string>),
    [choices, formatMessage, type, myId]
  );

  const [choice, all] = useMemo(() => [
    formatMessage({ id: `filter.${type}.label` }),
    required || isMultiple ? undefined : formatMessage({ id: `filter.${type}.all` })
  ], [type, required, isMultiple, formatMessage]);

  const renderValue = useCallback(
    (val: unknown): ReactNode => {
      if (isMultiple) {
        const ids = val as number[];
        if (!ids || ids.length === 0) return choice;
        return ids.length === 1
          ? names[toString(ids[0])]
          : formatMessage({ id: `filter.${type}.selected` }, { count: ids.length });
      }
      return val === 0 || val === '' ? choice : names[toString(val)];
    },
    [choice, names, isMultiple, type, formatMessage]
  );

  // MUI's aria-labelledby self-references the combobox, so without aria-label the name is the
  // element's own value. Defaults to the placeholder's label rather than making callers repeat it.
  const selectSlotProps: SelectProps['slotProps'] = useMemo(() => ({
    input: { 'aria-label': ariaLabel ? formatMessage({ id: ariaLabel, defaultMessage: ariaLabel }) : choice }
  }), [ariaLabel, choice, formatMessage]);

  const singleValue = (required && value === 0) || !choices ? '' : value;
  const selectValue = isMultiple ? selected : singleValue;
  const isPlaceholder = isMultiple ? selected.length === 0 : value === 0;

  return (
    <FormControl
        ref={ref}
        sx={controlSx}
        variant="outlined"
        size="small"
        error={error}
        disabled={disabled || size(choices) < 1}
        className={short ? shortRoot : root}
    >
      <Select
          id={`${type}-select`}
          sx={selectSx}
          slotProps={selectSlotProps}
          multiple={isMultiple}
          value={selectValue}
          displayEmpty
          renderValue={renderValue}
          onChange={handleChange}
          onClose={isMultiple ? handleClose : undefined}
          MenuProps={DEFAULT_MENU_PROPS}
          className={isPlaceholder ? placeholder : undefined}
      >
        {!isMultiple && all ? (
          <MenuItem value={0}>
            <Typography variant="h5">
              {all}
            </Typography>
          </MenuItem>
        ) : undefined}
        {myId ? (
          <MenuItem value={myId}>
            {renderValue(myId)}
          </MenuItem>
        ) : undefined}
        {displayChoices ? map(displayChoices, ({ id, name, title }) => (
          <MenuItem key={id} value={id}>
            {name || title}
          </MenuItem>
        )) : undefined}
      </Select>
      {helperText ? (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      ) : undefined}
    </FormControl>
  );
});

FilterSelector.displayName = 'FilterSelector';

export default memo(FilterSelector);
